import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { usePageMeta } from '../../lib/usePageMeta';

const SuccessPage: React.FC = () => {
    usePageMeta({ title: 'Payment Success', description: 'Your ProGeoData payment was processed successfully.', path: '/success' });
    const [loading, setLoading] = useState(true);
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const search = useSearch({ from: '/success' }) as { session_id?: string };
    const navigate = useNavigate();

    useEffect(() => {
        const id = search.session_id;
        if (!id) {
            setVerifyError('No session ID found. If you completed a purchase, please check your email.');
            setLoading(false);
            return;
        }

        fetch(`/api/checkout/verify?session_id=${id}`)
            .then(res => res.json())
            .then((data: any) => {
                if (data.success) {
                    setLoading(false);
                } else {
                    setVerifyError(data.error || 'Verification failed. Please contact support if you were charged.');
                }
                setLoading(false);
            })
            .catch(() => {
                setVerifyError('Could not verify your purchase. Please check your dashboard or contact support.');
                setLoading(false);
            });
    }, [search.session_id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-sky-500 animate-spin mx-auto mb-4" />
                    <h2 className="text-xl text-white font-bold">Verifying purchase...</h2>
                </div>
            </div>
        );
    }

    if (verifyError) {
        return (
            <div className="min-h-screen bg-slate-900 py-24 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="w-24 h-24 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
                        <AlertTriangle className="w-12 h-12 text-amber-500" />
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-6">Verification Issue</h1>
                    <p className="text-xl text-slate-300 mb-8">{verifyError}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button variant="primary" size="lg" onClick={() => navigate({ to: '/dashboard' })}>
                            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 py-24 px-4">
            <div className="max-w-3xl mx-auto text-center">
                <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle className="w-12 h-12 text-emerald-500" />
                </div>

                <h1 className="text-4xl font-bold text-white mb-6">Payment Successful!</h1>

                <p className="text-xl text-slate-300 mb-12">
                    Thank you for your purchase. Your account has been updated.
                </p>

                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 mb-12">
                    <h3 className="text-lg font-bold text-white mb-4">What happens next?</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                        <div className="p-4 bg-slate-900 rounded-lg">
                            <div className="font-bold text-sky-400 mb-2">1. Check Email</div>
                            <p className="text-sm text-slate-400">You'll receive a receipt and welcome email shortly.</p>
                        </div>
                        <div className="p-4 bg-slate-900 rounded-lg">
                            <div className="font-bold text-sky-400 mb-2">2. Access Dashboard</div>
                            <p className="text-sm text-slate-400">Log in to access your credits or download your data.</p>
                        </div>
                        <div className="p-4 bg-slate-900 rounded-lg">
                            <div className="font-bold text-sky-400 mb-2">3. Start Using</div>
                            <p className="text-sm text-slate-400">Your plan is active immediately.</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => navigate({ to: '/dashboard' })}
                    >
                        Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>

                    <Button
                        variant="outline"
                        size="lg"
                        onClick={() => navigate({ to: '/search' })}
                    >
                        Start Searching
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SuccessPage;
